import React, { useMemo } from 'react';
import './ResultTable.css';

function ResultTable({ language, villageData, formatCurrency }) {
  const formatNumber = useMemo(() => (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10
    }).format(num);
  }, []);

  const translations = useMemo(() => ({
    kannada: {
      oldCompensationTitle: 'ಹಳೆಯ ಕಾಯ್ದೆಯ ಪ್ರಕಾರ (ಪ್ರತಿ ಎಕರೆಗೆ)',
      oldCompensationText: 'ಸಾಮಾನ್ಯ ಐತೀರ್ಪು ಪರಿಹಾರದ ಮೊತ್ತ:',
      newCompensationTitle: 'ಅನುಮೋದಿತ ಹೊಸ ಪರಿಹಾರದ ಆಯ್ಕೆಗಳು (ಪ್ರತಿ ಎಕರೆಗೆ)',
      newCompensationOptions: [
        'ಸಂಧಾನಿತ ಮೊತ್ತ',
        'ಅಭಿವೃದ್ಧಿಪಡಿಸಿದ ವಸತಿ ನಿವೇಶಗಳು',
        'ಅಭಿವೃದ್ಧಿಪಡಿಸಿದ ವಾಣಿಜ್ಯ ನಿವೇಶಗಳು (65:35 ಅನುಪಾತದಲ್ಲಿ)',
        'ವರ್ಗಾವಣೆ ಮಾಡಬಹುದಾದ ಅಭಿವೃದ್ಧಿ ಹಕ್ಕುಗಳು (TDR)',
        'ಮಹಡಿ ಪ್ರದೇಶ ಅನುಪಾತ (FAR)'
      ],
      tdr: {
        notionalLandArea: 'Notional land area',
        guidelineValue: 'ಅಧಿಸೂಚಿಸಲಾದ ಜಮೀನಿನ ಮಾರ್ಗಸೂಚಿ ಮೌಲ್ಯ',
        caRate: 'ಸಕ್ಷಮ ಪ್ರಾಧಿಕಾರದ ವಸತಿ ನಿವೇಶನಗಳ ದರ:',
        laRate: 'ಸ್ಥಳೀಯ ಪ್ರಾಧಿಕಾರದ ವಸತಿ ನಿವೇಶನಗಳ ದರ:',
        combinedRate: 'ಸಕ್ಷಮ/ಸ್ಥಳೀಯ ಪ್ರಾಧಿಕಾರದ ವಸತಿ ನಿವೇಶನಗಳ ದರ:'
      }
    },
    english: {
      oldCompensationTitle: 'As per Old Act (per acre)',
      oldCompensationText: 'General Award Compensation Amount:',
      newCompensationTitle: 'Approved New Compensation Options (per acre)',
      newCompensationOptions: [
        'Negotiated amount',
        'Developed Residential Plots',
        'Developed Commercial plots (65:35 ratio)',
        'Transferable Development Rights (TDR)',
        'Floor Area Ratio (FAR)'
      ],
      tdr: {
        notionalLandArea: 'Notional land area',
        guidelineValue: 'Guidance value of notified land',
        caRate: 'Rate of residential plots (Competent Authority):',
        laRate: 'Rate of residential plots (Local Authority):',
        combinedRate: 'Rate of Residential Plots by Competent / Local Authority:'
      }
    }
  }), []);

  const t = translations[language];
  const isKannada = language === 'kannada';

  const village = villageData?.village;

  const tdrContent = useMemo(() => {
    if (!village?.tdr) return null;
    
    const caRate = village.tdr.ca_rate_per_sqm;
    const laRate = village.tdr.la_rate_per_sqm;
    const hasCaRate = caRate !== null && caRate !== undefined;
    const hasLaRate = laRate !== null && laRate !== undefined;
    
    if (!hasCaRate || !hasLaRate) {
      const availableRate = hasCaRate ? caRate : (hasLaRate ? laRate : null);
      if (availableRate !== null) {
        return (
          <div className="result-tdr-item">
            <div className="result-tdr-value">
              {t.tdr.combinedRate} <strong>₹{formatNumber(availableRate)}/{isKannada ? 'ಚ.ಮೀ.' : 'sq.m.'}</strong>
            </div>
          </div>
        );
      }
      return null;
    }
    
    return (
      <>
        <div className="result-tdr-item">
          <div className="result-tdr-value">
            {t.tdr.caRate} <strong>₹{formatNumber(caRate)}/{isKannada ? 'ಚ.ಮೀ.' : 'sq.m.'}</strong>
          </div>
        </div>
        <div className="result-tdr-item">
          <div className="result-tdr-value">
            {t.tdr.laRate} <strong>₹{formatNumber(laRate)}/{isKannada ? 'ಚ.ಮೀ.' : 'sq.m.'}</strong>
          </div>
        </div>
      </>
    );
  }, [village?.tdr, t.tdr, formatNumber, isKannada]);

  if (!villageData || !villageData.village) {
    return null;
  }

  return (
    <div className="result-table-container">
      <div className="result-old-container">
        <div className="result-old-header">
          {t.oldCompensationTitle}
        </div>
        <div className="result-old-content">
          {t.oldCompensationText} <strong>₹{formatNumber(village.old_general_award)}</strong>
        </div>
      </div>

      <div className="result-new-container">
        <div className="result-new-table-wrapper">
          <table className="result-new-table">
            <thead>
              <tr>
                <th colSpan="5" className="result-new-table-header">
                  {t.newCompensationTitle}
                </th>
              </tr>
              <tr>
                <th className="result-new-column-header">{t.newCompensationOptions[0]}</th>
                <th className="result-new-column-header">{t.newCompensationOptions[1]}</th>
                <th className="result-new-column-header">{t.newCompensationOptions[2]}</th>
                <th className="result-new-column-header">{t.newCompensationOptions[3]}</th>
                <th className="result-new-column-header">{t.newCompensationOptions[4]}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="result-new-cell">
                  <div className="result-new-cell-value">
                    ₹{formatNumber(village.new_compensation.consent_amount)}
                  </div>
                </td>
                <td className="result-new-cell">
                  <div className="result-new-cell-value">
                    {isKannada 
                      ? village.new_compensation.residential_note_kn.replace('ಅಭಿವೃದ್ಧಿಪಡಿಸಿದ ವಸತಿ ನಿವೇಶನಗಳನ್ನು ಪರಿಹಾರ ರೂಪದಲ್ಲಿ ನೀಡುವ ಬಗ್ಗೆ: ', '')
                      : village.new_compensation.residential_note_en}
                  </div>
                </td>
                <td className="result-new-cell">
                  <div className="result-new-cell-value">
                    {village.new_compensation.commercial_plots_sqft} {isKannada ? 'ಚ.ಅಡಿ' : 'sq. ft.'}
                  </div>
                </td>
                <td className="result-new-cell">
                  <div className="result-new-cell-value">
                    <div className="result-tdr-section">
                      <div className="result-tdr-content">
                        <div className="result-tdr-item">
                          <div className="result-tdr-label">{t.tdr.notionalLandArea}</div>
                          <div className="result-tdr-value">
                            <strong>{formatNumber(parseInt(village.tdr.nla_area_sqm))} {isKannada ? 'ಚ.ಮೀ.' : 'sq.m.'} ({formatNumber(village.tdr.nla_rate_per_sqf)} {isKannada ? 'ಚ.ಅಡಿ' : 'sq.ft.'})</strong>
                          </div>
                        </div>
                        <div className="result-tdr-item">
                          <strong><div className="result-tdr-label">{t.tdr.guidelineValue}</div></strong>
                        </div>
                        {tdrContent}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="result-new-cell">
                  <div className="result-new-cell-value">
                    {isKannada ? village.far.note_kn : village.far.note_en}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ResultTable;
